import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Alert, Notification } from 'rsuite';
import { TableProps } from 'rsuite/lib/Table';
import { IUseFilterOptions, useFilterSection, useModal } from '../../../../../components';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import * as services from '../../../../../components/FilterSection/services';
import { Exception } from '../../../../../data';
import { usePagination } from '../../../../../hooks';
import { selectorSuppliersExhibitionType, useSuppliersExhibitionStore } from '../../../../../store/useSuppliersExhibitionStore';
import { getNetworkCompetitorsInfo } from './NegociacaoFornecedor.networkCompetitors';
import {
  ROW_NOT_FOUND,
  competitorLoadingArea,
  findRowIndexByIdentity,
  resolveNextExpandedRowKeys,
  resolveRowIdentityKey,
} from './NegociacaoFornecedor.rowIdentity';
import { resolveVisibleFilters, stripHiddenFiltersFromRequestModel } from './filtrosPorVisao';
import { convertClusterParams } from '../../../../../utils/convertClusterParams';
import { extractApiErrorMessage } from '../../../../../utils/extractApiErrorMessage';
import { getSetupIpaBasicConfig } from '../../../../Administrador/Produtos/Components/SetupIpaBasicConfig/service';
import { SalvarNegociacaoSchemaProps } from '../../components';
import { useSelectedDatapoints } from '../../hooks';
import {
  AddProductToNegotiationProps,
  CriarNegociacaoArquivoPayload,
  DatapointsSelectionData,
  NetworkProductSelection,
  SalvarNegociacaoData,
  addProductToNegotiation,
  aplicarVerbaEmMassaNaBusca,
  criarNegociacaoComArquivo,
  desfazerAlteracaoDePrecos,
  downloadDatapointsNegotiation,
  getCompetitorsInfoSuppliers,
  getDatapointsNegotiation,
  patchNegotiationSaveEditedDatapoint,
  salvarNegociacao,
} from './NegociacaoFornecedor.services';
import * as T from './NegociacaoFornecedor.types';
import { AdicionarProdutoRefProps, CriarNegociacaoArquivoData, SalvarNegociacaoRefProps } from './components';
import {
  getCurrentInputCostForNegotiation,
  normalizeCostNegotiationMode,
  resolveLiquidCostForNegotiation,
  resolveWorkingLiquidCostForNegotiation,
} from './costNegotiation';
import { PMZNewCostCalc, getFiltrosModel, idealPurchasePriceOf, newPriceCompetitorsCalc } from './utils';
import { aplicarDerivadosDaGravacao, numerosQueATelaPodeAdiantar } from './derivadosDaGravacao';
import { linhaTemVerbaEmJogo } from './verbaDeSellIn';
import { CHAVE_DO_MODAL_DA_MASSA, edicoesDaMassaAplicada, linhasDaMassaAplicada } from './verbaEmMassa';
import type { EscolhaDaMassa, ResultadoDaMassa } from './verbaEmMassa';
import { useVerbaDeSellIn } from './verbaDeSellIn.hooks';
import type { VerbaParaGravar } from './verbaDeSellIn.hooks';

import IpaMath from '../../../../../lib/IPAMaths';

const DEFAULT_ORDER: T.SortStateProps = {
  type: 'productId',
  orderBy: 'asc',
};

// 
// ---------------------------------------------------------------------------------------------------
// O aviso de gravação com ressalva:

const PARTIAL_WRITE_WARNING_TITLE = 'Negociação salva com ressalva';

// Zero não é "sem duração":
const PARTIAL_WRITE_WARNING_DURATION = 0;

type ProductWriteCounts = {
  requestedProductCount: number;
  writtenProductCount: number;
};

/** Lê da resposta os dois números, e só devolve algo quando eles sustentam uma ressalva. */
const readPartialWriteCounts = (body?: Partial<ProductWriteCounts>): ProductWriteCounts | undefined => {
  const requestedProductCount = body?.requestedProductCount;
  const writtenProductCount = body?.writtenProductCount;

  if (typeof requestedProductCount !== 'number' || !Number.isInteger(requestedProductCount)) return undefined;
  if (typeof writtenProductCount !== 'number' || !Number.isInteger(writtenProductCount)) return undefined;
  if (writtenProductCount >= requestedProductCount) return undefined;

  return { requestedProductCount, writtenProductCount };
};

/** Monta o corpo do aviso a partir dos dois números, num ponto só. */
const buildPartialWriteWarning = ({ requestedProductCount, writtenProductCount }: ProductWriteCounts): string => {
  if (writtenProductCount === 0) return `Nenhum dos ${requestedProductCount} produtos selecionados foi gravado.`;
  if (writtenProductCount === 1) return `Dos ${requestedProductCount} produtos selecionados, 1 foi gravado.`;

  return `Dos ${requestedProductCount} produtos selecionados, ${writtenProductCount} foram gravados.`;
};

const DEFAULT_PAGINATION_OPTIONS = [
  {
    value: 20,
    label: 20,
  },
  {
    value: 40,
    label: 40,
  },
];

// Fábrica, e não constante compartilhada: vários tratadores desta tela escrevem numa posição da
// lista de linhas no lugar (`dataContent[index] = ...`). Com um objeto único de módulo, essa escrita
// alcançaria o estado inicial de toda montagem seguinte da tela.
const emptyNegotiationData = (): T.NegotiationDataProps => ({
  content: [],
  totalElements: 0,
  totalPages: 0,
});

const options: IUseFilterOptions = {
  page: 'NEGOTIATION_SUPPLIER',
  enableCascadingFilters: true,
  filters: [
    {
      key: 'productIds',
      placeholder: 'Produto',
      queryFn: services.listProducts,
      textLoading: 'Carregando produtos...',
    },
    {
      key: 'productFamilyIds',
      placeholder: 'Família',
      queryFn: services.listFamilies,
      textLoading: 'Carregando famílias...',
    },
    {
      key: 'storeIds',
      placeholder: 'Loja',
      queryFn: services.listStores,
      selectAll: true,
      textLoading: 'Carregando lojas...',
    },
    {
      key: 'clusters',
      placeholder: 'Cluster',
      queryFn: services.listClusters,
      selectAll: false,
      textLoading: 'Carregando clusters...',
    },
    {
      key: 'productBrand',
      placeholder: 'Marca',
      queryFn: services.listProductBrands,
      textLoading: 'Carregando marcas...',
    },
    {
      key: 'supplier',
      placeholder: 'Fornecedor',
      queryFn: services.listSuppliers,
      textLoading: 'Carregando fornecedores...',
    },
    {
      key: 'responsible',
      placeholder: 'Responsável',
      type: 'select',
      textSearch: 'Pesquisar',
      textLoading: 'Carregando responsáveis...',
    },
    {
      key: 'savedFilters',
      placeholder: 'Filtros salvos',
      queryFn: query => {
        return services.listSavedFilters(query, {
          screen: 'NEGOTIATION_SUPPLIER',
        });
      },
      type: 'select',
      textLoading: 'Carregando filtros...',
    },
  ],
};

export const useNegociacaoFornecedor = () => {
  const [data, setData] = useState<T.NegotiationDataProps>(emptyNegotiationData);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const suppliersExhibitionType = useSuppliersExhibitionStore(selectorSuppliersExhibitionType);

  const rowIdentityKey = resolveRowIdentityKey(suppliersExhibitionType);

  const isNetworkExhibition = suppliersExhibitionType === 'PRODUCT_SUPPLIERS_NETWORK';

  const filters = useFilterSection(options);

  // O recorte por visão é feito na lista que o gancho compartilhado DEVOLVE, e não na que ele
  // recebe.
  const filtersOfferedByExhibition = {
    ...filters,
    filters: resolveVisibleFilters(filters.filters, suppliersExhibitionType),
  };

  const previousSupplierExhibitionTypeRef = useRef(suppliersExhibitionType);

  useEffect(() => {
    const previousType = previousSupplierExhibitionTypeRef.current;
    const currentType = suppliersExhibitionType;

    if (previousType !== currentType) {
      filters.onClearAll();

      // As linhas em memória saem junto dos filtros, e é este esvaziamento que impede a grade de
      // desenhar a carga de um agrupamento sob a identidade de outro.
      setData(emptyNegotiationData());

      previousSupplierExhibitionTypeRef.current = currentType;
    }
  }, [suppliersExhibitionType, filters]);

  const [modalsState, { onHide: handleCloseModal, onOpen: handleOpenModal }] = useModal();

  const selectedDatapoints = useSelectedDatapoints();

  const { data: supplierNegotiationConfig } = useQuery({
    queryKey: ['ipa.setup.basicConfig'],
    queryFn: getSetupIpaBasicConfig,
  });

  const costNegotiationMode = normalizeCostNegotiationMode(supplierNegotiationConfig?.negotiation_cost);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [sort, setSort] = useState<T.SortStateProps>(DEFAULT_ORDER);

  const pagination = usePagination({
    totalElements: data?.totalElements,
  });

  const negociacaoModalRef = useRef<SalvarNegociacaoRefProps>(null);
  const adicionarProdutoRef = useRef<AdicionarProdutoRefProps>(null);

  const filtrosModel = stripHiddenFiltersFromRequestModel(
    getFiltrosModel(
      {
        ...filters.values,
        sort,
        size: pagination.pageSize,
        page: pagination.isOnInvalidLastIndex ? pagination.lastValidPage : pagination.activePage,
        suppliersExhibitionType,
      },
      'camel',
    ),
    suppliersExhibitionType,
  );

  // A limpeza passa de novo por aqui de propósito, e não é redundância: a conversão de parâmetros de
  // cluster renomeia a chave de cluster no caminho, e é esta versão do modelo que viaja em baixar,
  // salvar e adicionar.
  const filtrosModelWithoutSortAndPagination = stripHiddenFiltersFromRequestModel(
    convertClusterParams(_.omit(filtrosModel, ['sort', 'page', 'size', 'suppliersExhibitionType'])),
    suppliersExhibitionType,
  );

  // A seleção viaja por código de produto exatamente no agrupamento em que a identidade da linha JÁ É
  // o código do produto — derivar uma da outra é o que impede as duas de divergirem. Se um dia a
  // identidade da linha da rede mudar, o corpo enviado muda com ela, no mesmo lugar.
  const isSelectionByProductCode = rowIdentityKey === 'productId';

  /**
   * O que o comprador digitou no produto de código `productId`, se ele estiver na página que está na
   * tela.
   */
  const getTypedValuesOf = (productId: string): NetworkProductSelection => {
    const row = data.content.find(candidate => String(candidate.productId ?? '') === productId);

    return {
      productId,
      newObjectiveMargin: row?.newObjectiveMargin ?? undefined,
      newCost: row?.newCost ?? undefined,
      newPrice: row?.newPrice ?? undefined,
    };
  };

  /** A seleção que viaja no corpo de salvar e no de acrescentar produto a uma negociação salva. */
  const buildDatapointsSelection = (): DatapointsSelectionData => {
    if (!isSelectionByProductCode) {
      return {
        selectedIds: selectedDatapoints.data.selected,
        excludedIds: selectedDatapoints.data.excluded,
      };
    }

    const excludedNetworkProductIds = selectedDatapoints.data.excluded;

    const chosenProductIds = selectedDatapoints.data.selectedAll
      ? data.content
          .filter(row => row.newObjectiveMargin !== null || row.newCost !== null || row.newPrice !== null)
          .map(row => String(row.productId ?? ''))
          .filter(productId => productId !== '' && !excludedNetworkProductIds.includes(productId))
      : selectedDatapoints.data.selected;

    return {
      selectedIds: [],
      excludedIds: [],
      networkProducts: chosenProductIds.map(getTypedValuesOf),
      excludedNetworkProductIds,
    };
  };

  const downloadNegotiationModel = {
    filters: filtrosModelWithoutSortAndPagination,
    products: {
      selectedIds: selectedDatapoints.data.selected,
      excludedIds: selectedDatapoints.data.excluded,
    },
    suppliersExhibitionType,
    selectedAll: selectedDatapoints.data.selectedAll,
    sort: filtrosModel.sort,
  };

  // A captura foi acrescentada junto da do tratador de saída de campo, e pela mesma razão: este era
  // outro pedido que falhava sem dizer nada. Uma extração que falha deixava o comprador com o
  // indicador de carregamento apagando e nenhum arquivo, sem nenhuma mensagem que explicasse.
  // Nada aqui muda quando o botão é oferecido nem o que ele envia.
  const handleDownloadNegotitation = async () => {
    try {
      setIsDownloadLoading(true);
      return await downloadDatapointsNegotiation(downloadNegotiationModel);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      AlertWithTimestamp({ message: errorMessage ?? 'Erro ao baixar os produtos selecionados' });
      return undefined;
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const handleGetDatapointsSuccess = (data: T.SuppliersResponseProps) => {
    setExpandedRowKeys([]);
    setData(oldValue => ({ ...oldValue, ...data }));
  };

  const handleSortColumn = (sortColumn: TableProps['sortColumn'], sortType: TableProps['sortType']) => {
    setSort({
      orderBy: sortType,
      type: sortColumn,
    });
  };

  const updateCompetitorData = (rowIdentity: string, response: T.NegotiationDataItemProps['competitorsInfo']) => {
    setData(oldState => ({
      ...oldState,
      content: data.content.map(item => {
        if (item[rowIdentityKey] === rowIdentity) {
          return {
            ...item,
            competitorsInfo: response,
          };
        }
        return item;
      }),
    }));
  };

  const getInfoCompetitors = (rowData: T.NegotitationRowDataProps) => {
    const { storeId, productId, productFamilyId, competitorsPrice, productFamily, cluster } = rowData;
    const rowIdentity = rowData[rowIdentityKey];

    if (competitorsPrice !== 0 || productFamilyId) {
      const hasPreviousData = data.content.find(item => item[rowIdentityKey] === rowIdentity)?.competitorsInfo;

      if (hasPreviousData) return;

      const loadingArea = competitorLoadingArea(rowIdentity);

      // Na visão rede a linha consolida todas as lojas do cliente, e as duas fontes de detalhe de
      // concorrência respondem no grão de uma loja: o detalhe é buscado loja por loja e juntado no
      // navegador. Nas outras duas visões a linha JÁ é de uma loja ou de um cluster, e a chamada
      // continua sendo uma só, com o recorte que a linha carrega.
      const competitorsInfoRequest = isNetworkExhibition
        ? getNetworkCompetitorsInfo({
            productId,
            productFamilyId: productFamily ? productFamilyId : null,
            loadingArea,
          })
        : getCompetitorsInfoSuppliers(
            {
              productId,
              storeId,
              productFamilyId: productFamily ? productFamilyId : null,
              clusterId: cluster?.clusterId,
            },
            loadingArea,
          );

      competitorsInfoRequest
        .then(data => {
          if (data) updateCompetitorData(rowIdentity, data);
        })
        // Terceiro pedido do gancho que falhava calado. Sem isto, a linha expandida ficava para
        // sempre sem o detalhe de concorrência e sem nada que dissesse por quê — indistinguível de
        // produto sem concorrente cadastrado.
        .catch(error => {
          const errorMessage = extractApiErrorMessage(error);
          AlertWithTimestamp({ message: errorMessage ?? 'Erro ao carregar a concorrência do produto' });
        });
    }
  };

  const handleExpandedRow = (rowData: T.NegotitationRowDataProps) => {
    const { isOpen, nextExpandedRowKeys } = resolveNextExpandedRowKeys(expandedRowKeys ?? [], rowData[rowIdentityKey]);

    if (!isOpen) {
      getInfoCompetitors(rowData);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };

  const verba = useVerbaDeSellIn();

  const handleChangeObjectiveMargin = (index: number, value: string, rowData: T.SuppliersResponseContentProps) => {
    // Campo vazio é "sem edição"; zero digitado é margem editada para 0%, que vence o recuo.
    const newObjectiveMargin = value === '' ? null : Number(value.replace(/\./g, '').replace(',', '.'));
    const { bestIdealPrice, bestIdealPricePMZ } = idealPurchasePriceOf({ ...rowData, newObjectiveMargin }, costNegotiationMode);

    const dataContent = data?.content;

    dataContent[index] = {
      ...dataContent[index],
      newObjectiveMargin,
      bestIdealPrice,
      bestIdealPricePMZ,
    };
    setData(oldState => ({ ...oldState, content: dataContent }));
  };

  const handleChangeNewCost = (index: number, value: string, rowData: T.SuppliersResponseContentProps) => {
    const { salesTaxPercentage, expensesPercent } = rowData;

    const cost = Number(value.replace(/\./g, '').replace(',', '.'));
    const liquidCost = resolveLiquidCostForNegotiation(rowData as T.NegotiationDataItemProps, cost, costNegotiationMode);
    const PMZNewCost = PMZNewCostCalc(rowData, liquidCost);
    const newPriceCompetitors = rowData.newPrice !== null ? newPriceCompetitorsCalc(rowData, rowData.newPrice) : null;
    const hasCompetitorsPrice = rowData.competitorsPrice !== null && rowData.competitorsPrice > 0;

    const newCost = cost !== null && cost > 0 ? liquidCost : (rowData.cost ?? null);

    const newPriceMargin = rowData.newPrice !== null ? IpaMath.novaMargem(rowData.newPrice, salesTaxPercentage, expensesPercent, newCost) : null;
    const newCompetitorPriceMargin = hasCompetitorsPrice ? IpaMath.novaMargem(rowData.competitorsPrice, salesTaxPercentage, expensesPercent, newCost) : null;

    // O custo liquido acima e CEGO a verba, e os derivados que saem dele sairiam mais ALTOS que a
    // verdade numa linha com acordo. Com verba, a celula conserva o que tem e espera o motor.
    const temVerbaEmJogo = linhaTemVerbaEmJogo(rowData, verba.edicaoDaLinha(String(rowData[rowIdentityKey] ?? '')));

    const dataContent = data?.content;

    if (value === '0,00') {
      verba.descartarPorFaltaDeNovoCusto(String(rowData[rowIdentityKey] ?? ''));

      dataContent[index] = {
        ...dataContent[index],
        newCost: null,
        newPrice: null,
        newPriceMargin: null,
        newPriceCompetitors: null,
        PMZNewCost: null,
        marginCompetitorsPrice: null,
      };
    } else {
      dataContent[index] = {
        ...dataContent[index],
        newCost: cost ? cost : null,
        // Adiantados SO onde o calculo da tela nao mente; o que nao vem daqui a celula conserva.
        ...numerosQueATelaPodeAdiantar(temVerbaEmJogo, {
          PMZNewCost,
          newPriceMargin: newPriceMargin || null,
          marginCompetitorsPrice: newCompetitorPriceMargin,
        }),
        newPriceCompetitors: hasCompetitorsPrice ? newPriceCompetitors : null,
      };
    }
    setData(oldState => ({ ...oldState, content: dataContent }));
  };

  const handleChangeNewPrice = (index: number, value: string, rowData: T.SuppliersResponseContentProps) => {
    const { salesTaxPercentage, expensesPercent } = rowData;

    const newPrice = Number(value.replace(/\./g, '').replace(',', '.'));
    const currentCost = getCurrentInputCostForNegotiation(rowData as T.NegotiationDataItemProps, costNegotiationMode);
    const newCost = resolveWorkingLiquidCostForNegotiation(rowData as T.NegotiationDataItemProps, costNegotiationMode);
    const PMZNewCost = PMZNewCostCalc(rowData, newCost);
    const newPriceCompetitors = newPriceCompetitorsCalc(rowData, newPrice);
    const hasCompetitorsPrice = rowData.competitorsPrice !== null && rowData.competitorsPrice > 0;

    const newPriceMargin = newPrice !== null ? IpaMath.novaMargem(newPrice, salesTaxPercentage, expensesPercent, newCost) : null;
    const newCompetitorPriceMargin = hasCompetitorsPrice
      ? IpaMath.novaMargem(rowData.competitorsPrice ?? 0, salesTaxPercentage, expensesPercent, newCost)
      : null;

    const temVerbaEmJogo = linhaTemVerbaEmJogo(rowData, verba.edicaoDaLinha(String(rowData[rowIdentityKey] ?? '')));

    const dataContent = data?.content;

    if (value === '0,00') {
      dataContent[index] = {
        ...dataContent[index],
        newCost: null,
        newPrice: null,
        newPriceMargin: null,
        newPriceCompetitors: null,
        marginCompetitorsPrice: newCompetitorPriceMargin,
      };
      setData(oldState => ({ ...oldState, content: dataContent }));
    } else {
      dataContent[index] = {
        ...dataContent[index],
        newPrice,
        ...numerosQueATelaPodeAdiantar(temVerbaEmJogo, { PMZNewCost, newPriceMargin, marginCompetitorsPrice: newCompetitorPriceMargin }),
        newPriceCompetitors: hasCompetitorsPrice ? newPriceCompetitors : null,
      };
      setData(oldState => ({ ...oldState, content: dataContent }));
    }
  };

  // O corpo carrega AS DUAS identidades, e quem escolhe entre elas é o servidor pelo agrupamento que
  // viaja na string de consulta: identificador do ponto de dado nas visões por loja e por cluster, par
  // cliente e código do produto na visão rede, que não tem linha de loja a apontar. Mandar as duas em
  // toda visão evita um ramo por visão que ninguém exercita — o campo que não serve é ignorado.
  const getEditedDatapointModel = (
    rowData: T.NegotitationRowDataProps,
    // A verba vive no estado de edicao da grade, e nao na linha, entao chega por fora. Ausente e
    // "nao escreva a verba"; `sellinAllowance` vazia e "apague".
    verba?: Pick<T.NegotitationEditDatapointProps, 'sellinAllowance' | 'sellinAllowanceOrigin'> | null,
  ): T.NegotitationEditDatapointProps => ({
    id: rowData.productsToBePricedId,
    productId: rowData.productId,
    ...(verba ?? {}),
    newObjectiveMargin: rowData.newObjectiveMargin,
    newCost: rowData.newCost,
    newPrice: rowData.newPrice,
    newPriceMargin: rowData.newPriceMargin,
    newPriceCompetitors: rowData.newPriceCompetitors,
    bestIdealPrice: rowData.bestIdealPrice,
    bestIdealPricePMZ: rowData.bestIdealPricePMZ,
    PMZNewCost: rowData.PMZNewCost,
    costIndicator: rowData.costIndicator,
    visionSaved: rowData.visionSaved,
    cluster: rowData.cluster,
  });

  // Escreve a resposta do servidor na linha que está na tela. Qual linha é decidido pela busca por
  // identidade do módulo irmão: a chave muda com a visão, e um campo fixo chega ausente em toda linha
  // da visão rede — comparar dois ausentes escreveria o resultado de um produto em cima de outro.
  const findDataAndUpdate = async (rowIdentity: string, payload: Partial<T.NegotiationDataItemProps>) => {
    return new Promise<T.NegotiationDataItemProps>((resolve, reject) => {
      const index = findRowIndexByIdentity(data.content, rowIdentityKey, rowIdentity);

      if (index === ROW_NOT_FOUND) {
        // A identidade procurada entra na mensagem porque uma falha aqui é sempre a mesma pergunta:
        // qual linha o servidor respondeu que a página não tem.
        reject(new Error(`No row on the page carries the identity "${rowIdentity}"`));
        return;
      }

      const currentData = data.content;

      currentData[index] = {
        ...currentData[index],
        ...payload,
      };

      setData(oldState => ({ ...oldState, content: currentData }));

      resolve(data.content[index]);
    });
  };

  const handleInputBlur = async (
    rowData: T.NegotitationRowDataProps,
    _index: number,
    details: {
      onBlurValue: number | null;
      onFocusValue: number | null;
      hasChanges: boolean;
    },
    verbaSobrescrita?: VerbaParaGravar | null,
  ) => {
    if (!details.hasChanges) return;

    // Sair do campo grava nas TRÊS visões: o servidor identifica a linha da rede por cliente e código
    // do produto. A captura existe porque a falha era silenciosa — o cliente HTTP não registra
    // interceptador de resposta, e sem ela o comprador via o PMZ recalcular, que é local, e ia embora
    // acreditando que o valor tinha sido gravado.
    try {
      // Verba já gravada NÃO viaja de novo: a escrita no motor é parcial por presença de chave, e
      // reenviar a do líquido sem o valor ao fornecedor apaga a âncora que o comprador digitou.
      const model = getEditedDatapointModel(rowData, verbaSobrescrita ?? verba.verbaPendenteDeGravacao(rowData[rowIdentityKey] as string));
      const res = await patchNegotiationSaveEditedDatapoint(model, suppliersExhibitionType);
      // Chave ausente na resposta nao entra no patch, e a celula conserva o que ja tem.
      const patch = aplicarDerivadosDaGravacao(res);

      await findDataAndUpdate(rowData[rowIdentityKey], {
        isEdited: res.isEdited,
        costIndicator: res.costIndicator,
        ...patch,
      });

      // O MESMO patch vai para a edição local: em verba sugerida o motor a recalcula, e linha e
      // edição desencontradas deixam o campo, os pontos e o gap no valor de antes.
      verba.marcarComoGravada(String(rowData[rowIdentityKey] ?? ''), patch);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      AlertWithTimestamp({ message: errorMessage ?? 'Erro ao salvar alteração do produto' });
    }
  };

  /** Sair do campo le a verba da edicao pendente; aplicar a ideal a PASSA, no mesmo clique. */
  const handleVerbaBlur = async (rowData: T.NegotitationRowDataProps, verbaSobrescrita?: VerbaParaGravar | null) => {
    const verbaParaGravar = verbaSobrescrita ?? verba.verbaPendenteDeGravacao(rowData[rowIdentityKey] as string);

    if (!verbaParaGravar) return;

    await handleInputBlur(rowData, 0, { onBlurValue: null, onFocusValue: null, hasChanges: true }, verbaParaGravar);
  };

  const handleResetPageState = () => {
    filters.onClearAll();
    selectedDatapoints.reset();
    selectedDatapoints.toggleSelectedAll(false);
  };

  const handleSaveNegotiation = async (formData: SalvarNegociacaoSchemaProps) => {
    const model: SalvarNegociacaoData = {
      name: formData.name,
      supplier: formData.supplier,
      startDate: formData.startDate,
      endDate: formData.endDate,
      datapoints: buildDatapointsSelection(),
      selectedAll: selectedDatapoints.data.selectedAll,
      filters: filtrosModelWithoutSortAndPagination,
      suppliersExhibitionType,
    } as const;

    // A promessa é DEVOLVIDA, e não disparada e esquecida. Nada muda para o comprador: o que muda é que
    // esta função, declarada assíncrona, deixa de dizer "terminei" antes de a requisição ter respondido.
    // Quem a chama passa a poder esperar o desfecho.
    return salvarNegociacao(model)
      .then(response => {
        if (response.status !== 201) return;

        // O ramo de sucesso tem dois desfechos, e a diferença entre eles é o que o comprador precisa
        // saber: gravou tudo, ou gravou parte. O sucesso limpo é o de sempre, palavra por palavra.
        const partialWrite = readPartialWriteCounts(response.data);

        if (partialWrite) {
          Notification.warning({
            title: PARTIAL_WRITE_WARNING_TITLE,
            description: buildPartialWriteWarning(partialWrite),
            duration: PARTIAL_WRITE_WARNING_DURATION,
          });
        } else {
          Notification.success({
            description: 'Negociação salva com sucesso!',
          });
        }

        // O resto do desfecho é idêntico nos dois casos: a gravação aconteceu, e a tela termina de
        // salvar do mesmo jeito. Sucesso parcial não é falha.
        handleCloseModal('salvarNegociacao');
        negociacaoModalRef.current?.handleReset();
        handleResetPageState();
      })
      .catch(error => {
        const errorMessage = extractApiErrorMessage(error);
        if (errorMessage) {
          AlertWithTimestamp({ message: errorMessage });
          return;
        }
        AlertWithTimestamp({ message: 'Erro ao salvar negociação' });
      });
  };

  const handleCriarNegociacaoComArquivo = async (formData: CriarNegociacaoArquivoData) => {
    if (!formData.nivel || !formData.file) return;

    const payload: CriarNegociacaoArquivoPayload = {
      name: formData.name,
      supplier: formData.supplier,
      startDate: formData.startDate,
      nivel: formData.nivel,
      selectedIds: formData.selectedIds,
      file: formData.file,
      escolhasDaVerba: formData.escolhasDaVerba,
    };

    criarNegociacaoComArquivo(payload)
      .then(response => {
        if (response.status === 202) {
          handleCloseModal('criarNegociacaoArquivo');
          handleOpenModal('processandoArquivo');
        }
      })
      .catch(error => {
        const status = error?.response?.status;
        if (status === 400) {
          handleCloseModal('criarNegociacaoArquivo');
          handleOpenModal('erroProcessamentoArquivo');
        } else {
          const errorMessage = extractApiErrorMessage(error);
          if (errorMessage) {
            AlertWithTimestamp({ message: errorMessage });
          } else {
            const { message } = new Exception('Erro ao criar negociação com arquivo');
            AlertWithTimestamp({ message });
          }
        }
      });
  };

  const handleAddProductToNegotiation = async (formData: { id: string }) => {
    const model: AddProductToNegotiationProps = {
      negotiationId: formData.id,
      filters: filtrosModelWithoutSortAndPagination,
      datapoints: buildDatapointsSelection(),
      selectedAll: selectedDatapoints.data.selectedAll,
    };

    addProductToNegotiation(model, suppliersExhibitionType)
      .then(response => {
        if (response.status !== 200) return;
        handleCloseModal('adicionarProdutoNegociacao');
        adicionarProdutoRef.current?.reset();
        Notification.success({
          description: 'Produto adicionado à negociação',
        });
      })
      .catch(error => {
        const errorMessage = extractApiErrorMessage(error);

        if (errorMessage) {
          AlertWithTimestamp({ message: errorMessage });
        } else {
          const { message } = new Exception('Erro ao adicionar produto à negociação');
          AlertWithTimestamp({ message });
        }
      });
  };

  const quantidadeSelecionada = selectedDatapoints.data.selectedAll
    ? data.totalElements - selectedDatapoints.data.excluded.length
    : selectedDatapoints.data.selected.length;

  /** Cada item aplicado escreve a célula E a LINHA. A grade NÃO é recarregada. */
  const adotarVerbaEmMassa = (resultado: ResultadoDaMassa) => {
    const patchesPorLinha = linhasDaMassaAplicada(resultado);

    if (Object.keys(patchesPorLinha).length > 0) {
      setData(anterior => ({
        ...anterior,
        content: anterior.content.map(linha => {
          const patch = patchesPorLinha[String((linha as Record<string, unknown>)[rowIdentityKey] ?? '')];

          return patch ? { ...linha, ...patch } : linha;
        }),
      }));
    }

    verba.adotarEdicoes(edicoesDaMassaAplicada(resultado));
  };

  /** A seleção é montada pela MESMA função que "salvar" e "adicionar à negociação salva" usam. */
  const handleAplicarVerbaEmMassa = (escolha: EscolhaDaMassa): Promise<ResultadoDaMassa> =>
    aplicarVerbaEmMassaNaBusca(
      {
        ...escolha,
        filters: filtrosModelWithoutSortAndPagination,
        datapoints: buildDatapointsSelection(),
        selectedAll: selectedDatapoints.data.selectedAll,
      },
      suppliersExhibitionType,
    );

  // A repetição automática continua DESLIGADA, e isso é escolha:
  const { fetchStatus, isError, refetch } = useQuery({
    queryKey: ['get-negociacao', filters.values, sort, pagination.activePage, pagination.pageSize, suppliersExhibitionType],
    queryFn: async () => getDatapointsNegotiation(filtrosModel),
    retry: false,
    onSuccess: handleGetDatapointsSuccess,
  });

  const isTableDataLoading = !(fetchStatus === 'idle');

  const wasGridLoadFailingRef = useRef(false);

  // Ao entrar em falha, a grade esvazia o corpo da tabela — as linhas que estavam ali são de uma
  // carga que não é mais a atual.
  useEffect(() => {
    const wasFailing = wasGridLoadFailingRef.current;
    wasGridLoadFailingRef.current = isError;

    // Só a ENTRADA na falha limpa. Limpar enquanto a falha permanece tiraria do comprador uma
    // seleção que ele tivesse acabado de fazer sobre a carga seguinte.
    if (!isError || wasFailing) return;

    selectedDatapoints.reset();
    selectedDatapoints.toggleSelectedAll(false);
    setExpandedRowKeys([]);
  }, [isError, selectedDatapoints]);

  // Chamada sem argumento: a nova busca aceita um objeto de opções, e repassar o evento de clique
  // como primeiro argumento seria entregar a ela um objeto que não é o que ela espera.
  const handleRetryGridLoad = () => {
    void refetch();
  };

  const handleDesfazerAlteracaoDePrecos = async (rowData: T.NegotitationRowDataProps) => {
    try {
      const res = await desfazerAlteracaoDePrecos(rowData, suppliersExhibitionType);
      if (!res) return;
      await findDataAndUpdate(rowData[rowIdentityKey], res);
      Alert.success('Alteração de preço desfeita com sucesso');
    } catch {
      const { message } = new Exception('Erro ao desfazer alteração de preço');
      AlertWithTimestamp({ message });
    }
  };

  const handleStatusChange = async (newStatus: T.NegotiationSupplierDatapointStatus, updatedRowData: T.NegotitationRowDataProps) => {
    // Update the local data immediately
    await findDataAndUpdate(updatedRowData[rowIdentityKey], {
      negotiationSupplierDatapointStatus: newStatus,
      isEdited: updatedRowData.isEdited,
    });
  };

  return {
    filters: filtersOfferedByExhibition,
    quantidadeSelecionada,
    table: {
      data,
      isLoading: isTableDataLoading,
      isError,
      onRetry: handleRetryGridLoad,
      rowKey: rowIdentityKey,
      // Esta tela sabe montar o detalhe de concorrência da linha de rede, buscando loja por loja. A
      // grade é compartilhada com a tela de negociação salva, que ainda NÃO sabe: lá o pedido sairia
      // sem loja nenhuma, e o comprador veria um card que não é da linha que ele abriu. Por isso o
      // botão da visão rede é oferecido por quem tem a busca, e não pela grade.
      offersNetworkCompetitionDetail: true,
      expandedRowKeys,
      sortColumn: sort.type,
      selectedDatapoints,
      pagination: {
        lengthMenu: DEFAULT_PAGINATION_OPTIONS,
        activePage: pagination.activePage,
        displayLength: pagination.pageSize,
        total: data?.totalElements ?? 0,
        onChangePage: pagination.handleChangePage,
        onChangeLength: pagination.handleChangeLength,
      },
      onSortColumn: handleSortColumn,
      onExpandRow: handleExpandedRow,
      onChangeNewCost: handleChangeNewCost,
      onChangeNewPrice: handleChangeNewPrice,
      onChangeObjectiveMargin: handleChangeObjectiveMargin,
      onBlur: handleInputBlur,
      onStatusChange: handleStatusChange,
      verba,
      onVerbaBlur: handleVerbaBlur,
      costNegotiationMode,
      contextMenu: {
        onUndoChanges: handleDesfazerAlteracaoDePrecos,
      },
    },
    handlers: {
      download: handleDownloadNegotitation,
      isDownloadLoading,
      save: handleSaveNegotiation,
      openModal: handleOpenModal,
      closeModal: handleCloseModal,
    },
    modals: {
      salvarNegociacao: {
        show: modalsState?.salvarNegociacao,
        handleOpen: () => handleOpenModal('salvarNegociacao'),
        onHide: () => handleCloseModal('salvarNegociacao'),
        onSubmit: handleSaveNegotiation,
      },
      adicionarProdutoNegociacao: {
        ref: adicionarProdutoRef,
        show: modalsState.adicionarProdutoNegociacao,
        onHide: () => handleCloseModal('adicionarProdutoNegociacao'),
        onSubmit: handleAddProductToNegotiation,
      },
      adicionarVerbaEmMassa: {
        show: modalsState[CHAVE_DO_MODAL_DA_MASSA],
        quantidadeSelecionada,
        enviar: handleAplicarVerbaEmMassa,
        onHide: () => handleCloseModal(CHAVE_DO_MODAL_DA_MASSA),
        onAplicado: adotarVerbaEmMassa,
      },
      criarNegociacaoArquivo: {
        show: modalsState.criarNegociacaoArquivo,
        onHide: () => handleCloseModal('criarNegociacaoArquivo'),
        onSubmit: handleCriarNegociacaoComArquivo,
      },
      processandoArquivo: {
        show: modalsState.processandoArquivo,
        onHide: () => handleCloseModal('processandoArquivo'),
      },
      erroProcessamentoArquivo: {
        show: modalsState.erroProcessamentoArquivo,
        onHide: () => handleCloseModal('erroProcessamentoArquivo'),
        onTentarNovamente: () => {
          handleCloseModal('erroProcessamentoArquivo');
          handleOpenModal('criarNegociacaoArquivo');
        },
      },
    },
  };
};
